import React from 'react';
import { hydrateRoot } from 'react-dom/client';

import { Header } from '@hz-design-system/web-ui';
import { I18nContextProvider } from '@ecg-internal/js-react-i18n';

import ExpiredListingApp from '../../apps/ExpiredListingApp/ExpierdListingApp';

import { trackGAEvent } from '../../utils/gaTracking';

import '../../../scss/global.scss';
import '../../../scss/layout.scss';

// server side only components styles for this page
import '../../../../server/pages/eVip.styles';

function onRecoverableError(error, errInfo) {
  const context = {
    componentStack: undefined,
  };

  if (errInfo?.componentStack) {
    const errorBoundaryError = new Error(error.message);
    errorBoundaryError.name = `React ErrorBoundary ${errorBoundaryError.name}`;
    errorBoundaryError.stack = errInfo.componentStack;

    error.cause = errorBoundaryError;

    context.componentStack = errInfo.componentStack;
  }

  if (window.DD_RUM?.addError) {
    window.DD_RUM.addError(error, { context });
  }
}

export default (i18n) => {
  const headerConfig = window.__HEADER_CONFIG__ || {};
  const environment = window.__ENV_CONFIG__ || {};
  const appState = window.__CONFIG__ || {};
  const { isHydrateErrorLoggingEnabled } = environment.experiments ?? {};

  const smartBannerAndroidRoot = document.getElementById('smart-banner-android-root');
  const headerRootContainer = document.getElementById('header-root');
  const ExpierdListingRoot = document.getElementById('expired-listing-root');
  const evipSimilarItemsRoot = document.getElementById('evip-similar-items-root');
  const evipSellerOtherItemsRoot = document.getElementById('evip-seller-other-items-root');
  const evipFranchiseDealerItemsRoot = document.getElementById('evip-franchise-dealer-items-root');

  // Render smart banner for Android
  // in case when this is not an Android there is no element to hydrate
  if (environment?.hasSmartBannerAndroidEnabled) {
    const loadComponent = async () => {
      try {
        const SmartBannerAndroidApp = (
          await import(
            /* webpackChunkName: "SmartBannerAndroidApp" */ '../../apps/SmartBannerAndroidApp/SmartBannerAndroidApp'
          )
        ).default;
        hydrateRoot(smartBannerAndroidRoot!, <SmartBannerAndroidApp />);
      } catch {}
    };
    loadComponent();
  }

  const hydrateOptions = { onRecoverableError: isHydrateErrorLoggingEnabled ? onRecoverableError : () => {} };

  // Header is server side rendered in express-react library
  hydrateRoot(headerRootContainer!, <Header headerConfig={headerConfig} trackGAEvent={trackGAEvent} />, hydrateOptions);

  hydrateRoot(ExpierdListingRoot!, <ExpiredListingApp itemId={appState.listing.itemId} />, hydrateOptions);

  if (evipSimilarItemsRoot) {
    import('../../apps/EvpSimilarItemsApp/EvipSimilarItemsApp').then(({ default: EvipSimilarItemsApp }) =>
      hydrateRoot(
        evipSimilarItemsRoot,
        <I18nContextProvider i18nInstance={i18n}>
          <EvipSimilarItemsApp environment={environment} eVipSimilarItems={appState.eVipSimilarItems} />
        </I18nContextProvider>,
        hydrateOptions,
      ),
    );
  }

  const eVipSellerOtherItems = appState.eVipSellerOtherItems;

  if (evipSellerOtherItemsRoot && eVipSellerOtherItems) {
    import('../../apps/EVipSellerOtherItemCarouselApp/EVipSellerOtherItemCarouselApp').then(
      ({ default: EVipSellerOtherItemCarouselApp }) =>
        hydrateRoot(
          evipSellerOtherItemsRoot,
          <I18nContextProvider i18nInstance={i18n}>
            <EVipSellerOtherItemCarouselApp {...eVipSellerOtherItems} />
          </I18nContextProvider>,
          hydrateOptions,
        ),
    );
  }

  const eVipFranchiseDealerItems = appState.eVipFranchiseDealerItems;

  if (evipFranchiseDealerItemsRoot && eVipFranchiseDealerItems) {
    const loadFranchiseDealerItems = async () => {
      try {
        const { default: EVipFranchiseDealerItemCarouselApp } = await import(
          /* webpackChunkName: "EVipFranchiseDealerItemCarouselApp" */ '../../apps/EVipFranchiseDealerItemCarouselApp/EVipFranchiseDealerItemCarouselApp'
        );
        hydrateRoot(
          evipFranchiseDealerItemsRoot,
          <I18nContextProvider i18nInstance={i18n}>
            <EVipFranchiseDealerItemCarouselApp {...eVipFranchiseDealerItems} />
          </I18nContextProvider>,
          hydrateOptions,
        );
      } catch {}
    };
    loadFranchiseDealerItems();
  }
};
